import React, { useState, useEffect, useRef } from "react";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Divider from "@mui/material/Divider";
import { Typography, Menu,  Badge, Tooltip, Box } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CheckIcon from '@mui/icons-material/Check';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import "../../Styles/Sidebar.css";
import "../../Styles/Header.css";
import Sidebar from "../SidePannel/Sidebar";
import LightLogo from "../../Images/P21_Light_logo.png";
import { GetNotification, ChangeNotificationStatus, ChangeAllNotificationStatus } from '../../Redux/Actions/userStatusService'
import { useDispatch } from "react-redux";
import {
  AppBar,
  Drawer,
  TextTypography,
  StatusText,
  UserIcons,
} from "./AppbarCommon";
import { io } from "socket.io-client";
import { Base_URL } from "../../Common/Path";
import { useNavigate } from 'react-router-dom';

function Appbar(props) {
  const [colors, setcolors] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = React.useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const entityIdCheckIntervalRef = useRef(null);
  const hasInitializedRef = useRef(false); // Track if we've already initialized

  // Get user info from localStorage
  const userInfoString = localStorage.getItem("userInfo");
  const userInfo = userInfoString && userInfoString !== "undefined" ? JSON.parse(userInfoString) : null;
  const role = userInfo?.portal || 'MANAGER'; // Default to MANAGER if portal not found
  const token = localStorage.getItem("token");

  // Function to fetch notifications
  const fetchNotifications = async (entityId) => {
    // Check if entityId is missing, null, undefined, or the string "undefined"
    if (!entityId || entityId === "undefined" || entityId === "null") {
      return;
    }
    
    try {
      const response = await dispatch(GetNotification({
        role: role,
        entityId: entityId
      }));
      
      if (response?.payload?.data) {
        const sortedNotifications = sortNotifications(response.payload.data);
        setNotifications(sortedNotifications);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // Check for entityId and fetch notifications
  useEffect(() => {
    if (!userInfo || !role || hasInitializedRef.current) return;
    
    // First try to get entityId directly
    let entityId = localStorage.getItem("entId");
    
    // Check if entityId is valid (not null, undefined, or the string versions)
    if (entityId && entityId !== "undefined" && entityId !== "null") {
      fetchNotifications(entityId);
      hasInitializedRef.current = true; // Mark as initialized
      return;
    }
    
    // If entityId is not available or invalid, set up an interval to check for it
    entityIdCheckIntervalRef.current = setInterval(() => {
      entityId = localStorage.getItem("entId");
      if (entityId && entityId !== "undefined" && entityId !== "null") {
        fetchNotifications(entityId);
        hasInitializedRef.current = true; // Mark as initialized
        clearInterval(entityIdCheckIntervalRef.current);
      }
    }, 1000); // Check every second
    
    return () => {
      if (entityIdCheckIntervalRef.current) {
        clearInterval(entityIdCheckIntervalRef.current);
      }
    };
  }, [userInfo, role, dispatch]);

  // Socket connection - also needs to check for entityId
  useEffect(() => {
    if (!token || !userInfo?.portal || socketRef.current) return; // Skip if socket already exists
    
    const setupSocket = (entityId) => {
      // Check if entityId is valid
      if (!entityId || entityId === "undefined" || entityId === "null") return;
      
      if (!socketRef.current) {
        socketRef.current = io(Base_URL, {
          auth: {
            token,
            role: userInfo.portal,
            parentRoleId: entityId
          },
          transports: ['websocket'],
          reconnection: true,
          reconnectionAttempts: 50
        });

        socketRef.current.on('connect', () => {
          console.log("Connected");
        });

        socketRef.current.on('messageReceived', (notifications) => {
          const notificationArray = Array.isArray(notifications) ? notifications : [notifications];
          
          if (Notification.permission === 'granted') {
            notificationArray.forEach(notification => {
              new Notification(notification.title || 'New Notification', {
                body: notification.message || notification.body,
                icon: '/favicon/favicon-32x32.png'
              });
            });
          }

          setNotifications(prev => {
            const newNotifications = [...notificationArray, ...prev];
            return sortNotifications(newNotifications);
          });
        });

        socketRef.current.on('disconnect', () => {
          console.log("Disconnected");
        });

        socketRef.current.on('connect_error', (error) => {
          console.error("Connection error:", error);
        });
      }
    };
    
    // First try to get entityId directly
    let entityId = localStorage.getItem("entId");
    
    // Check if entityId is valid
    if (entityId && entityId !== "undefined" && entityId !== "null") {
      setupSocket(entityId);
      return;
    }
    
    // If entityId is not available or invalid, set up an interval to check for it
    const socketCheckInterval = setInterval(() => {
      entityId = localStorage.getItem("entId");
      if (entityId && entityId !== "undefined" && entityId !== "null") {
        setupSocket(entityId);
        clearInterval(socketCheckInterval);
      }
    }, 1000); // Check every second
    
    return () => {
      clearInterval(socketCheckInterval);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [token, userInfo?.portal]);

  // Sort notifications function - unread first, then by date
  const sortNotifications = (notifs) => {
    if (!notifs) return [];
    
    return [...notifs].sort((a, b) => {
      if (!a.isRead && b.isRead) return -1;
      if (a.isRead && !b.isRead) return 1;
      
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const unreadCount = notifications?.filter(notification => !notification?.isRead)?.length;

  const handleNotificationClick = async (notification) => {
    try {
      // Only mark as read if it's not already read
      if (!notification.isRead) {
        const response = await dispatch(ChangeNotificationStatus({ 
          id: notification._id, 
          body: { isRead: true } 
        }));

        if (response?.payload?.statusCode === 200) {
          // Update the notification in the state
          setNotifications(prevNotifications => {
            const updatedNotifications = prevNotifications.map(item => 
              item._id === notification._id ? { ...item, isRead: true } : item
            );
            return sortNotifications(updatedNotifications);
          });
        }
      }
    } catch (error) {
      console.error("Error changing notification status:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await dispatch(ChangeAllNotificationStatus());
      if (response?.payload?.statusCode === 200) {
        // Update all notifications to read in the state
        setNotifications(prevNotifications => {
          const updatedNotifications = prevNotifications.map(item => ({ ...item, isRead: true }));
          return sortNotifications(updatedNotifications);
        });
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHrs / 24);

    if (diffMins < 60) {
      return `${diffMins} mins ago`;
    } else if (diffHrs < 24) {
      return `${diffHrs} hr ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return `${diffDays} days ago`;
    }
  };

  const handleSeeAllClick = () => {
    handleClose(); // Close the notification menu
    navigate('/allnotification'); // Navigate without page reload
  };

  return (
    <>
      <AppBar position="absolute" open={open} style={{boxShadow:"none", marginBottom:"50px"}}>
        <Toolbar
          sx={{
            pr: "24px",
            boxShadow: 'none'
          }}
        >
          <IconButton
            edge="start"
            color="black"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: "36px",
              color:'black',
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {props.title}
            <p style={{ fontSize: "12px", color: "#000" }}>
              <b style={{ color: "gray" }}>{props.mainpage}</b>-{props.subpage}
            </p>
          </Typography>

          <TextTypography />
          <StatusText colors={colors} status={status} />
          <IconButton onClick={handleClick}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon style={{ color: "#757575" }} />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              style: {
                width: '350px',
                maxHeight: '520px',
                borderRadius: '8px',
                padding: 0,
                overflow: 'hidden'
              },
            }}
          >
            <div style={{ 
              padding: '15px 20px', 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="h6" style={{ fontWeight: 'bold', fontSize: '16px' }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={handleMarkAllAsRead}>
                    <DoneAllIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {notifications?.length === 0 ? (
                <div style={{ padding: '15px 20px', textAlign: 'center' }}>
                  No Notification Found
                </div>
              ) : (
                notifications?.map((notification) => (
                  <div 
                    key={notification._id} 
                    onClick={() => handleNotificationClick(notification)}
                    style={{ 
                      padding: '16px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: notification.isRead ? "transparent" : "#f5f5f5"
                    }}
                  >
                    <div 
                      style={{ 
                        width: '36px', 
                        height: '36px', 
                        borderRadius: '50%', 
                        backgroundColor: '#2196f3', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginRight: '12px',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}
                    >
                      {notification.isRead ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <SouthEastIcon fontSize="small" />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                          style={{ 
                            fontWeight: 'bold', 
                            fontSize: '14px',
                            color: '#333',
                            lineHeight: 1.4
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography 
                          style={{ 
                            fontSize: '14px', 
                            color: '#666',
                            lineHeight: 1.4
                          }}
                        >
                          {notification.message}
                        </Typography>
                      </div>
                    </div>
                    <div>
                      <Typography 
                        style={{ 
                          fontSize: '12px', 
                          color: '#888',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {formatTimeAgo(notification.createdAt)}
                      </Typography>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div 
              style={{ 
                textAlign: 'center', 
                padding: '10px 0',
                margin: '5px 0',
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#fff',
                height: '45px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={handleSeeAllClick}
            >
              <Typography 
                style={{ 
                  color: '#2196f3', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                See All
              </Typography>
            </div>
          </Menu>
          <UserIcons />
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: [1],
            py:[2],
            color: "white",
            backgroundColor:'#FFFFFF',
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent:"center", gap: 1}}>
            <Box
              sx={{
                width: '100%',
                height: 55,
                backgroundColor: "white",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "4px", 
              }}
            >
              <img 
                src={ LightLogo}
                alt="Plutus21" 
                style={{
                  height: "100%",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>
          {/* <br />
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon style={{ color: "white" }} />
          </IconButton> */}
          {/* {open && (
            <p style={{
              position: "absolute", 
              bottom: "-20px",
              right: "54px",
              fontSize: "12px", 
              color: "white"
            }}> 
              Manager Panel
            </p>
          )} */}
        </Toolbar>
        <Divider sx={{mt:1}} />
        <Sidebar open={open} setOpen={setOpen} />
      </Drawer>
    </>
  );
}

export default Appbar;